import type { VulnerabilityMap } from '../types/problems';

export function getVulnerabilityMap(
  vulnerability: string,
  decisionSeat: string = 'S'
): VulnerabilityMap {
  const vuln = vulnerability.toLowerCase();

  switch (vuln) {
    case 'neither':
    case 'none':
      return { N: false, E: false, S: false, W: false };

    case 'both':
    case 'all':
      return { N: true, E: true, S: true, W: true };

    case 'ns':
    case 'n-s':
    case 'north-south':
    case 'south-north':
      return { N: true, E: false, S: true, W: false };

    case 'ew':
    case 'e-w':
    case 'east-west':
    case 'west-east':
      return { N: false, E: true, S: false, W: true };

    case 'favorable':
      // We are not vulnerable, opponents are
      if (decisionSeat === 'N' || decisionSeat === 'S') {
        return { N: false, E: true, S: false, W: true };
      } else {
        return { N: true, E: false, S: true, W: false };
      }

    case 'unfavorable':
      // We are vulnerable, opponents are not
      if (decisionSeat === 'N' || decisionSeat === 'S') {
        return { N: true, E: false, S: true, W: false };
      } else {
        return { N: false, E: true, S: false, W: true };
      }

    default:
      // Default to neither vulnerable
      return { N: false, E: false, S: false, W: false };
  }
}

// Raw vulnerability values that getVulnerabilityMap can interpret. Anything
// outside this set is genuinely "unknown" and should fall back to the
// "Unknown Vulnerability" label rather than silently rendering "Neither".
const KNOWN_VULNERABILITIES = new Set([
  'neither', 'none',
  'both', 'all',
  'ns', 'n-s', 'north-south', 'south-north',
  'ew', 'e-w', 'east-west', 'west-east',
  'favorable',
  'unfavorable',
]);

/**
 * Derive the single-word vulnerability label (Jeff/decision-seat perspective)
 * from the SAME raw `vulnerability` value the indicator board renders from.
 *
 * This deliberately reuses getVulnerabilityMap so the text label can never
 * disagree with the coloured board: "Neither", "Both", "Favorable",
 * "Unfavorable". Returns null when the raw value is unknown/empty so the
 * caller can show "Unknown Vulnerability".
 */
export function getVulnerabilityLabel(
  vulnerability: string | null | undefined,
  decisionSeat: string = 'S'
): string | null {
  const vuln = (vulnerability ?? '').toLowerCase();
  if (!KNOWN_VULNERABILITIES.has(vuln)) {
    return null;
  }

  const map = getVulnerabilityMap(vulnerability ?? '', decisionSeat);
  const onNS = decisionSeat === 'N' || decisionSeat === 'S';
  const ourVuln = onNS ? map.N : map.E;
  const oppVuln = onNS ? map.E : map.N;

  if (ourVuln && oppVuln) return 'Both';
  if (!ourVuln && !oppVuln) return 'Neither';
  return ourVuln ? 'Unfavorable' : 'Favorable';
}
